import { Controller } from "@hotwired/stimulus";
import { announce, fillTemplate } from "../utils/announce";
import { KeyedTimers } from "../utils/keyed_timers";
import { ListenerSet } from "../utils/listener_set";
import { MicrotaskCoalescer } from "../utils/microtask_coalescer";
import { NUMBER_BOUNDS, type NumberValueConstraints } from "../utils/number_bounds";
import { NumberValueReader } from "../utils/number_value";
import { SafeTimeout } from "../utils/safe_timeout";
import { TransientHooks } from "../utils/transient_hooks";
import {
  type ConfirmedCableSubscription,
  createConfirmedSubscription,
  identifierOf,
  parseSubscriptionParams,
} from "./consumer";

/** The hooks a connection may find written by an earlier, now-gone one. */
const TRANSIENT = new TransientHooks({
  attributes: ["data-typing", "data-typing-indicator-rejected"],
});

/** Milliseconds of silence after which a typer is dropped. */
const DEFAULT_TIMEOUT = 3000;
/** Minimum milliseconds between outgoing typing signals. */
const DEFAULT_THROTTLE = 2000;

/**
 * Headless **typing indicator** — a *server-bound* behavior: a controller whose
 * state lives on the server stream, not in client memory. Typing in the composer
 * throttle-broadcasts a `typing` signal over an Action Cable channel; received
 * signals from *other* clients render "X is typing…" into a visible `status` slot
 * and auto-clear after `timeout` ms of silence. The whole behavior is HTML + a
 * broadcast — no app JS, no client store, no reconciler. Ships in the opt-in
 * `stimeo-ui/cable` subpath (`@rails/actioncable` optional peer); the core stays
 * zero-dep.
 *
 * Markup contract (identifier: `stimeo--typing-indicator`):
 *   <div data-controller="stimeo--typing-indicator"
 *        data-stimeo--typing-indicator-channel-value="TypingChannel"
 *        data-stimeo--typing-indicator-params-value='{"room":"chat_42"}'
 *        data-stimeo--typing-indicator-name-value="Alice"
 *        data-stimeo--typing-indicator-announce-one-text-value="{name} is typing">
 *     <textarea data-stimeo--typing-indicator-target="input"></textarea>
 *     <p data-stimeo--typing-indicator-target="status"
 *        data-one="{name} is typing…" data-many="{names} are typing…"></p>
 *   </div>
 *
 * Server contract (a trivial rebroadcast channel):
 *   class TypingChannel < ApplicationCable::Channel
 *     def subscribed = stream_from "typing:#{params[:room]}"
 *     def typing(data) = ActionCable.server.broadcast("typing:#{params[:room]}",
 *                                                     { name: data["name"] })
 *   end
 *
 * The `status` target is a plain visible slot, not a live region: assistive tech is
 * reached through the page's shared announcer instead, so a set that settles is sent
 * there as `announceOneText` / `announceManyText` (`{name}` / `{names}` / `{count}`,
 * debounced). Both default to empty, and an empty message announces nothing — a page
 * that wants the indicator seen but not heard simply leaves them off.
 *
 * `change` dispatches `{ names }`.
 *
 * @remarks
 * Behavior only — the indicator's look is the consumer's CSS, keyed off the
 * `data-typing` hook; the copy is localizable through the `data-one` /
 * `data-many` templates (`{name}` / `{names}` / `{count}`), with terse English
 * as the fallback. The own echo is
 * dropped by comparing the broadcast `name` against `name` (a same-name guard,
 * not authentication — identity belongs to the server). Sending tracks the full
 * subscription lifecycle (via the shared confirmation-aware subscription):
 * signals are dropped before confirmation and while the connection is down, so
 * an outage never burns the throttle window on undeliverable sends. A refused
 * subscription publishes the `data-typing-indicator-rejected` hook (cleared on
 * `connect()` — rejection is transient server state), mirroring
 * `data-live-counter-rejected`. Typing state is transient by nature:
 * `connect()` resets the hook and the status slot (a Turbo cache snapshot must
 * not resurrect a stale "X is typing…"), and re-population happens naturally
 * from the stream. The subscription, the per-typer timers, and the delegated
 * `input` listener are all released on `disconnect()` (Turbo navigation
 * included).
 *
 * The declared `channel` names the subscription: `params` *adds* identifier
 * parameters except `channel`, which is ignored. Without a declared channel
 * nothing is subscribed whatever `params` holds.
 *
 * The subscription follows the declaration: a `channel` or `params` change moves
 * it — once, after the batch, however many of the two changed together, and never
 * before `connect()` or after `disconnect()` — and drops the typers, their expiry
 * timers, a pending announcement, the throttle window and the rejected hook
 * without an event, as a fresh connection would, since none of them is a fact of
 * the new room. A `name` change keeps the subscription and the typers, except one
 * shown under the new name: that is this client now, so it is dropped with its
 * expiry and the display redrawn, without a `change` — it did not stop typing.
 * The own echo is still filtered by the current `name`. `timeout` arms each
 * typer's expiry when its signal arrives, so a change applies from that typer's
 * next signal and never moves a deadline already set.
 */
export class TypingIndicatorController extends Controller<HTMLElement> {
  /** Numeric read boundaries share one reader for this controller instance. */
  readonly #numbers = new NumberValueReader();

  static override targets = ["input", "status"];
  static override values = {
    channel: { type: String, default: "" },
    params: { type: String, default: "" },
    name: { type: String, default: "" },
    timeout: { type: Number, default: DEFAULT_TIMEOUT },
    // Must stay below `timeout`: the throttle is leading-edge only (no trailing
    // send), so a receiver's display survives continuous typing only while a
    // fresh signal lands within its timeout window.
    throttle: { type: Number, default: DEFAULT_THROTTLE },
    announceOneText: { type: String, default: "" },
    announceManyText: { type: String, default: "" },
  };

  static valueConstraints = {
    timeout: NUMBER_BOUNDS.timer,
    throttle: NUMBER_BOUNDS.nonNegative,
  } satisfies NumberValueConstraints<typeof TypingIndicatorController.values>;
  static events = ["change"] as const;

  declare readonly hasStatusTarget: boolean;
  declare readonly statusTarget: HTMLElement;
  declare channelValue: string;
  declare paramsValue: string;
  declare nameValue: string;
  declare timeoutValue: number;
  declare throttleValue: number;
  declare announceOneTextValue: string;
  declare announceManyTextValue: string;

  /** Delay (ms) before one settled typer set is sent to the shared announcer. */
  static readonly #announceDelay = 200;

  #subscription: ConfirmedCableSubscription | null = null;
  readonly #follow = new MicrotaskCoalescer(() => this.#followDeclaration());
  /** The identifier the subscription was last built for. */
  #identifier = "";
  /** Names currently typing (other clients), in the order their first signal arrived. */
  readonly #typers = new Set<string>();
  /** Each typer's auto-clear timer, restarted by every further signal from that name. */
  readonly #expiry = new KeyedTimers<string>();
  readonly #timers = new SafeTimeout();
  /** Epoch ms of the last broadcast, for leading-edge throttling. */
  #lastSentAt = 0;
  /** The one outstanding announcement, so a newer set supersedes it. */
  #announceId: number | null = null;
  /**
   * Whether this controller is between `connect()` and `disconnect()`. Stimulus
   * delivers target callbacks before `connect()` and after `disconnect()`, where
   * the status is teardown's to leave empty.
   */
  #connected = false;
  /** The delegated input listener of one connection. */
  readonly #listeners = new ListenerSet();

  /** Moves the subscription to the declared channel. */
  channelValueChanged(): void {
    this.#follow.schedule();
  }

  /** Moves the subscription to the identifier the parameters name. */
  paramsValueChanged(): void {
    this.#follow.schedule();
  }

  /** Stops showing a typer under the name this client now declares. */
  nameValueChanged(): void {
    this.#follow.schedule();
  }

  /**
   * The identifier this declaration names: the declared channel plus the other
   * parameters, whose own `channel` key is left out. A malformed `params`
   * declaration falls back to no parameters, so the identifier keeps naming the
   * channel instead of the subscription never being created at all.
   */
  get #descriptor(): Record<string, unknown> {
    const { channel: _declared, ...rest } = parseSubscriptionParams(this.paramsValue);
    return { channel: this.channelValue, ...rest };
  }

  /**
   * Paints the current copy into a `status` target that arrived at runtime.
   *
   * No event: the set of typers did not change, only the element that displays it.
   * Without this a region swapped in mid-conversation stays empty while `data-typing`
   * still says someone is typing, leaving the state in the visual hook alone. An empty
   * set paints the empty copy, so a region that arrives with an old one is cleared.
   */
  statusTargetConnected(): void {
    if (this.#connected) this.#paintStatus([...this.#typers]);
  }

  /** Paints the current copy, empty when nobody types, into the `status` target that stays. */
  statusTargetDisconnected(): void {
    if (this.#connected) this.#paintStatus([...this.#typers]);
  }

  override connect(): void {
    this.#connected = true;
    // Typing state is transient: a Turbo cache snapshot must not resurrect a
    // stale indicator, and the live stream re-populates naturally. Rejection is
    // transient server state too — the fresh subscription re-decides the hook.
    this.#reset();

    // Delegated on the container so the composer needs no per-input data-action
    // (and swapped/appended inputs keep working).
    this.#listeners.add(this.element, "input", this.#onInput);
    this.#follow.activate();
    this.#open();
  }

  override disconnect(): void {
    this.#connected = false;
    this.#follow.cancel();
    this.#listeners.dispose();
    this.#close();
    this.#reset();
  }

  /**
   * Opens the subscription the declaration names, or none without a channel.
   *
   * @stimeoRuntimeOnly `channel` and `params` name the one subscription this call opens.
   */
  #open(): void {
    const descriptor = this.#descriptor;
    this.#identifier = identifierOf(descriptor);
    if (!this.channelValue) return;
    // Confirmation tracking (connected / disconnected / rejected) lives in the
    // shared subscription; #onInput gates on its `confirmed` so an outage
    // doesn't burn the throttle window on dropped sends.
    this.#subscription = createConfirmedSubscription(descriptor, {
      // The server refused the subscription: the send gate stays shut for
      // good, and the hook lets the consumer's CSS reflect the dead stream.
      rejected: () => {
        this.element.setAttribute("data-typing-indicator-rejected", "true");
      },
      received: (data: unknown) => this.#onReceived(data),
    });
  }

  /** Releases the subscription {@link #open} opened. */
  #close(): void {
    this.#subscription?.unsubscribe();
    this.#subscription = null;
  }

  /**
   * Restarts the cycle when the declaration names a different identifier, and
   * otherwise drops a typer shown under this client's own name.
   *
   * @stimeoRuntimeOnly `channel` and `params` are compared with what the subscription was
   *   built from, and `name` picks the typer that is this client now.
   */
  #followDeclaration(): void {
    if (identifierOf(this.#descriptor) !== this.#identifier) {
      this.#close();
      this.#reset();
      this.#open();
    } else {
      this.#forget(this.nameValue);
    }
  }

  /** Drops a typer that stopped being another client, reporting no change. */
  #forget(name: string): void {
    if (!this.#typers.delete(name)) return;
    this.#expiry.clear(name);
    this.#paint();
  }

  /** Clears the transient typing state (every cycle's start + disconnect teardown). */
  #reset(): void {
    this.#timers.clearAll();
    this.#expiry.clearAll();
    this.#announceId = null;
    this.#typers.clear();
    TRANSIENT.reset(this.element);
    if (this.hasStatusTarget) this.statusTarget.textContent = "";
    this.#lastSentAt = 0;
  }

  /** Throttled (leading-edge) broadcast of this client's typing signal. */
  readonly #onInput = (): void => {
    // Before the confirmed subscription a perform() is silently dropped by
    // Action Cable — and recording #lastSentAt would ALSO throttle away the
    // first real send, so bail without touching the throttle state.
    if (!this.#subscription?.confirmed) return;
    const now = Date.now();
    if (now - this.#lastSentAt < this.#throttle) return;
    this.#lastSentAt = now;
    this.#subscription.perform("typing", { name: this.nameValue });
  };

  /**
   * Tracks a broadcast typer. The own echo is dropped (same `name`); every
   * further signal from a name restarts its auto-clear timer, so the indicator
   * survives continuous typing and clears `timeout` ms after the last signal.
   *
   * @stimeoRuntimeOnly `name` filters this client's own echo and `timeout` arms one typer's expiry;
   *   the typers shown come from what is received.
   */
  #onReceived(data: unknown): void {
    const name = (data as { name?: unknown } | null)?.name;
    if (typeof name !== "string" || name === "" || name === this.nameValue) return;

    const added = !this.#typers.has(name);
    this.#typers.add(name);
    this.#expiry.set(name, () => this.#untrack(name), this.#timeout);
    if (added) this.#render();
  }

  /**
   * The silence after which a typer is dropped, in ms: a finite, non-negative number
   * a timer can hold. Anything else names no delay — `setTimeout` reads `NaN`, a
   * negative value and `Infinity` alike as "now", and a value past
   * the maximum platform timer delay overflows to the same place — so the typer would
   * vanish in the same task it appeared and the indicator could never be seen. Such
   * a declaration falls back to the default.
   */
  get #timeout(): number {
    return this.#safeTimeout;
  }

  /**
   * The minimum gap between outgoing signals, in ms: a finite, non-negative number.
   * Anything else names no interval, and the gate then settles the same way at every
   * keystroke — `NaN` and a negative gap leave it open, so every keystroke broadcasts,
   * while `Infinity` is never exceeded, so nothing is ever sent. Such a declaration
   * falls back to the default.
   */
  get #throttle(): number {
    return this.#safeThrottle;
  }

  #untrack(name: string): void {
    this.#typers.delete(name);
    this.#render();
  }

  /**
   * Reflects the typer set onto the display, announces it, and emits `change`.
   */
  #render(): void {
    const names = this.#paint();
    this.#announce(names);
    this.dispatch("change", { detail: { names } });
  }

  /**
   * Hands the settled typer set to the page's shared announcer.
   *
   * Debounced: a burst of arrivals is one announcement, not one per name. Only a
   * non-empty set is announced — that typing stopped is not news worth interrupting
   * a reader for, and the visible copy already clears. Wording comes from the
   * consumer, and {@link announce} drops an empty message, so an undeclared template
   * announces nothing at all.
   *
   * @stimeoRuntimeOnly The wording Values are read to compose one announcement; the
   * visible typer list is rendered from the set, not from them.
   */
  #announce(names: string[]): void {
    if (this.#announceId !== null) this.#timers.clear(this.#announceId);
    this.#announceId = null;
    if (names.length === 0) return;

    const message =
      names.length === 1
        ? fillTemplate(this.announceOneTextValue, { name: names[0] ?? "" })
        : fillTemplate(this.announceManyTextValue, {
            names: names.join(", "),
            count: names.length,
          });

    this.#announceId = this.#timers.set(() => {
      announce(message);
      this.#announceId = null;
    }, TypingIndicatorController.#announceDelay);
  }

  /** Writes the current typer set onto the hook and the status slot. */
  #paint(): string[] {
    const names = [...this.#typers];
    this.element.setAttribute("data-typing", names.length > 0 ? "true" : "false");
    this.#paintStatus(names);
    return names;
  }

  /** Writes the copy for `names` into the status slot, when there is one. */
  #paintStatus(names: string[]): void {
    if (this.hasStatusTarget) this.statusTarget.textContent = this.#message(names);
  }

  /**
   * Builds the status copy. Localizable through `data-one` / `data-many`
   * templates on the status target (`{name}` / `{names}` / `{count}`); terse
   * English is the fallback.
   */
  #message(names: string[]): string {
    if (names.length === 0) return "";
    const joined = names.join(", ");
    if (names.length === 1) {
      const template = this.statusTarget.dataset.one;
      const name = names[0] ?? "";
      return template ? fillTemplate(template, { name }) : `${name} is typing…`;
    }
    const template = this.statusTarget.dataset.many;
    return template
      ? fillTemplate(template, { names: joined, count: names.length })
      : `${joined} are typing…`;
  }
  /** Current `timeout` declaration resolved against its numeric contract. */
  get #safeTimeout(): number {
    return this.#numbers.read(
      this,
      "timeout",
      this.timeoutValue,
      TypingIndicatorController.values.timeout.default,
      TypingIndicatorController.valueConstraints.timeout,
    );
  }

  /** Current `throttle` declaration resolved against its numeric contract. */
  get #safeThrottle(): number {
    return this.#numbers.read(
      this,
      "throttle",
      this.throttleValue,
      TypingIndicatorController.values.throttle.default,
      TypingIndicatorController.valueConstraints.throttle,
    );
  }
}
